import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import { Gift, RefreshCw, Globe2, MapPin, Download } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const PERIODS = [
  { v: 0, label: "Tout" },
  { v: 30, label: "30 j" },
  { v: 90, label: "90 j" },
  { v: 180, label: "6 mois" },
  { v: 365, label: "12 mois" },
];

function Seg({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 gap-1" data-testid={testid}>
      {options.map((o) => (
        <button key={String(o.v)} type="button" onClick={() => onChange(o.v)}
          data-testid={`${testid}-${o.v}`}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
            value === o.v ? "bg-findo-indigo text-white" : "text-slate-600 hover:bg-slate-50"
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="h-8 w-8 grid place-items-center rounded-lg bg-emerald-100 text-emerald-600"><Icon size={16} /></div>
      <div>
        <p className="text-[11px] text-slate-500 font-medium leading-none">{label}</p>
        <p className="font-display text-lg font-extrabold text-findo-navy leading-tight">{value}</p>
      </div>
    </div>
  );
}

export default function FreeUnlocksPanel() {
  const [days, setDays] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api(`/admin/analytics/free-unlocks?days=${days}`)
      .then((r) => setData(r))
      .catch(() => toast.error("Chargement des déblocages gratuits impossible"))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  async function exportCsv() {
    try {
      const res = await fetch(`${API}/admin/analytics/free-unlocks/export.csv?days=${days}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Export impossible");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `findloop-deblocages-offerts-${days ? days + "j" : "tout"}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }

  const total = data?.total ?? 0;
  const countries = data?.countries || [];
  const maxCount = countries.reduce((m, c) => Math.max(m, c.count), 0) || 1;

  return (
    <div className="space-y-5" data-testid="free-unlocks-panel">
      <div className="flex flex-col gap-1">
        <h3 className="font-display text-xl font-extrabold text-findo-navy flex items-center gap-2">
          <Gift size={20} className="text-emerald-600" /> Déblocages gratuits — Offre de lancement (Afrique)
        </h3>
        <p className="text-sm text-slate-500">Nombre de déblocages de coordonnées offerts aux utilisateurs africains (B2C-B2C), par pays. Offre temporaire en attendant Flutterwave.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Seg options={PERIODS} value={days} onChange={setDays} testid="free-unlocks-filter-period" />
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={exportCsv} disabled={loading || total === 0} data-testid="free-unlocks-export-csv"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            <Download size={14} /> Export CSV
          </button>
          <button type="button" onClick={load} data-testid="free-unlocks-refresh"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            {loading ? <FindoSpinner size={14} /> : <RefreshCw size={14} />} Actualiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <MiniStat icon={Gift} label="Déblocages offerts" value={total} />
        <MiniStat icon={Globe2} label="Pays actifs" value={countries.length} />
        <MiniStat icon={MapPin} label="Top pays" value={countries[0]?.name || "—"} />
      </div>

      <div className="rounded-2xl border border-border bg-white card-shadow p-5" data-testid="free-unlocks-list">
        {loading && !data ? (
          <div className="py-10 grid place-items-center text-slate-400"><FindoSpinner size={22} /></div>
        ) : total === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="free-unlocks-empty">Aucun déblocage gratuit sur cette période.</p>
        ) : (
          <ul className="space-y-3.5">
            {countries.map((c) => (
              <li key={c.code} data-testid={`free-unlock-row-${c.code}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-700">{c.name} <span className="text-slate-400 font-normal">({c.code})</span></span>
                  <span className="text-xs font-bold text-slate-500">{c.count} · {c.pct}%</span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${Math.max(4, (c.count / maxCount) * 100)}%` }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
